import { useState } from 'react';
import * as Yup from 'yup';
import { Typography, Grid, Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { Formik, ErrorMessage, Form, useFormikContext } from 'formik';
import FormikFields from './FormikFields';
import validationObject from './ValidationSchema';

const FormActionButtons = ({
  buttonTitle,
  buttonPlacement,
  isNarrowButtons,
  isMobile,
  isNotDisable,
  defaultEnabledButton,
  setButtonExternallyDisabled,
  clickProp,
  setClickedOn,
  setClickedButton,
  setIsErrorField,
  handleOtherButton
}) => {
  const { isSubmitting, resetForm, validateForm, setTouched } = useFormikContext();

  const showRequiredFieldErrors = async () => {
    setIsErrorField(true);
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setTouched(
        Object.keys(errors).reduce(
          (acc, fieldName) => ({ ...acc, [fieldName]: true }),
          {}
        ),
        true
      );
      return false;
    }
    setIsErrorField(false);
    return true;
  };

  return (
    <Grid
      container
      gap={{ md: 2, xs: 1.5 }}
      mt={{ sm: 2, xs: 3 }}
      flexWrap="wrap"
      sx={
        isNarrowButtons
          ? {
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }
            }
          : buttonPlacement === 'right'
            ? {
                display: 'flex',
                justifyContent: { sm: 'flex-end', xs: 'stretch' }
              }
            : { flexDirection: { xs: 'column', sm: 'row' } }
      }
    >
      {buttonTitle.map((btn) => {
        const btnDisabled =
          setButtonExternallyDisabled === btn
            ? true
            : defaultEnabledButton && defaultEnabledButton?.includes(btn)
              ? false
              : isSubmitting;

        return btn !== 'Cancel' && btn !== 'Return To Home' ? (
          <Button
            key={btn}
            variant="contained"
            color={btn === 'Reject' ? 'error' : 'primary'}
            sx={{
              background:
                !isNotDisable && btnDisabled
                  ? 'lightgray !important'
                  : btn === 'Reject' && !btnDisabled
                    ? '#d32f2f !important'
                    : '',
              px: { md: '20px', xs: 1 },
              py: { md: '8px', xs: 0.8 },
              fontSize: { md: '0.875rem', xs: '13px' },
              width: isMobile ? '100%' : 'auto'
            }}
            type={btn === 'Reject' ? 'button' : 'submit'}
            onClick={async (e) => {
              setClickedButton(btn);
              if (clickProp) {
                setClickedOn(btn);
              }
              if (btn === 'Reject') {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              const isValid = await showRequiredFieldErrors();
              if (!isValid) {
                e.preventDefault();
              }
            }}
            disabled={!isNotDisable && btnDisabled}
          >
            {btn}
          </Button>
        ) : (
          <Button
            key={btn}
            variant="outlined"
            sx={{
              display:
                btn === 'Cancel' && buttonTitle?.length === 1 ? 'none' : '',
              px: { md: '20px', xs: 1 },
              py: { md: '8px', xs: 0.8 },
              fontSize: { md: '0.875rem', xs: '13px' },
              width: isMobile ? '100%' : 'auto'
            }}
            color="primary"
            onClick={() => {
              resetForm();
              handleOtherButton();
            }}
          >
            {btn}
          </Button>
        );
      })}
    </Grid>
  );
};

const FormikForm = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNarrowButtons = useMediaQuery('(max-width:430px)');

  // Construct initial values object
  const {
    alignmentDirection,
    allFields,
    buttonTitle,
    fullScreen,
    content,
    formMethod,
    setClickedOn,
    clickProp,
    handleOtherButton,
    buttonPlacement,
    defaultEnabledButton,
    innerRef,
    setButtonExternallyDisabled,
  } = props;
  const [isErrorField, setIsErrorField] = useState(false);
  const [clickedButton, setClickedButton] = useState('');

  const initialValues = {};
  allFields?.forEach((field) => {
    if (field?.dependencyFields?.length > 0) {
      field?.dependencyFields?.forEach((dependencyFields) => {
        if (dependencyFields?.initialValue !== undefined) {
          initialValues[dependencyFields?.fieldName] =
            dependencyFields?.initialValue;
        } else if (dependencyFields?.fieldType === 'check') {
          initialValues[dependencyFields?.fieldName] = false;
        }
      });
    }
    initialValues[field?.fieldName] =
      field?.fieldType === 'check'
        ? field?.initialValue ?? false
        : field?.initialValue ?? '';
  });

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={Yup.object(validationObject(Yup, allFields))}
        onSubmit={(values, { setSubmitting }) => {
          // Don't submit form if Reject button was clicked - it's handled separately
          if (clickedButton !== 'Reject') {
            formMethod(values);
          }
          setSubmitting(false);
        }}
        innerRef={innerRef}
      >
        {() => (
          <Form>
            <Grid
              container
              spacing={{ xs: 1.5, md: 2 }}
              direction={alignmentDirection ?? 'row'}
            >
              {allFields?.map((field) => (
                <>
                  {field.heading && (
                    <Grid item xs={12}>
                      <Typography variant="h5" ml={0.5}>
                        {field.heading}
                      </Typography>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    lg={fullScreen || field.br ? 12 : 6}
                    key={field.fieldName}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'start'
                    }}
                  >
                    <Box
                      sx={{
                        width: field.br ? { xs: '100%', sm: '49.5%' } : '100%'
                      }}
                    >
                      {field.aboveParagraph && (
                        <Typography mb={2}>{field.aboveParagraph}</Typography>
                      )}

                      <FormikFields
                        initialValue={field.initialValue}
                        fieldLabel={
                          <>
                            {field.fieldLabel}{' '}
                            {field.required && (
                              <span style={{ color: 'red' }}>*</span>
                            )}
                          </>
                        }
                        placement={field.placement}
                        placeHolder={field.placeHolder}
                        fieldName={field.fieldName}
                        listUI={field?.listUI}
                        fieldType={field.fieldType}
                        options={field?.options}
                        disabled={field.disabled}
                        isErrorField={isErrorField}
                        dependency={field.dependency}
                        multiSelect={field.multiSelect}
                        multipleTagInput={field.multipleTagInput}
                        lengthLimit={field.lengthLimit}
                        existingFiles={field.existingFiles}
                        helpIcon={field.helpIcon} // Add this
                        helpContent={field.helpContent} 
                        descriptions={field.descriptions}
                        loading={field.loading}
                      />

                      {field.belowParagraph && (
                        <Typography mb={2} mt={{ xs: 2, sm: 0 }}>
                          {field.belowParagraph}
                        </Typography>
                      )}

                      {!field.loading && (
                        <Typography
                          sx={{ color: '#8B0000', fontSize: 14, margin: 1 }}
                        >
                          <ErrorMessage
                            name={field.fieldName}
                            render={(msg) => (
                              <div style={{ color: '#a72a2a' }}>{msg}</div>
                            )}
                          />
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {field?.dependencyFields?.map((childField) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        lg={fullScreen || childField.br ? 12 : 4}
                        key={childField.fieldName}
                        width={alignmentDirection ? '50%' : ''}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'start'
                        }}
                      >
                        {childField.aboveParagraph && (
                          <Typography mb={2}>
                            {childField.aboveParagraph}
                          </Typography>
                        )}

                        {childField.heading && (
                          <Typography variant="h5" ml={0.5} mb={2}>
                            {childField.heading}
                          </Typography>
                        )}

                        <FormikFields
                          initialValue={childField.initialValue}
                          fieldLabel={
                            <>
                              {childField.fieldLabel}{' '}
                              {childField.required && (
                                <span style={{ color: 'red' }}>*</span>
                              )}
                            </>
                          }
                          placement={childField.placement}
                          placeHolder={childField.placeHolder}
                          fieldName={childField.fieldName}
                          listUI={childField?.listUI}
                          fieldType={childField.fieldType}
                          options={childField?.options}
                          disabled={childField.disabled}
                          isErrorField={isErrorField}
                          dependency={childField.dependency}
                          multiSelect={childField.multiSelect}
                          multipleTagInput={childField.multipleTagInput}
                          existingFiles={field.existingFiles}
                          helpIcon={field.helpIcon} // Add this
                          helpContent={field.helpContent} 
                          descriptions={field.descriptions}
                          loading={childField.loading}
                        />

                        {childField.belowParagraph && (
                          <Typography mb={2}>
                            {childField.belowParagraph}
                          </Typography>
                        )}

                        {!childField.loading && (
                          <Typography
                            sx={{ color: '#8B0000', fontSize: 14, margin: 1 }}
                          >
                            <ErrorMessage
                              name={childField.fieldName}
                              render={(msg) => (
                                <div style={{ color: '#a72a2a' }}>{msg}</div>
                              )}
                            />
                          </Typography>
                        )}
                      </Grid>
                    );
                  })}
                </>
              ))}
            </Grid>

            {typeof content === 'function' ? content() : content}
            <FormActionButtons
              buttonTitle={buttonTitle}
              buttonPlacement={buttonPlacement}
              isNarrowButtons={isNarrowButtons}
              isMobile={isMobile}
              isNotDisable={props.isNotDisable}
              defaultEnabledButton={defaultEnabledButton}
              setButtonExternallyDisabled={setButtonExternallyDisabled}
              clickProp={clickProp}
              setClickedOn={setClickedOn}
              setClickedButton={setClickedButton}
              setIsErrorField={setIsErrorField}
              handleOtherButton={handleOtherButton}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default FormikForm;
